import { Link } from "@inertiajs/react"
import { Eye, Truck } from "lucide-react"
import { useTranslation } from "react-i18next"

import { MarkShippedDialog } from "@/components/shipments/mark-shipped-dialog"
import { Button, buttonVariants } from "@/components/ui/button"
import { shippableVariantsFor } from "@/lib/shippable-variants"
import { cn } from "@/lib/utils"
import { brandShipmentsPath } from "@/routes"
import type { BrandMissionCollabRow } from "@/types/missions"
import { SUBMISSION_CAP } from "@/types/submissions"

import { AcceptApplicationConfirmDialog } from "./accept-application-confirm-dialog"
import { ApproveReopenConfirmDialog } from "./approve-reopen-confirm-dialog"
import { DeclineReopenConfirmDialog } from "./decline-reopen-confirm-dialog"
import { FlagIssueDialog } from "./flag-issue-dialog"
import { MarkCompleteConfirmDialog } from "./mark-complete-confirm-dialog"
import { MarkDeliveredConfirmDialog } from "./mark-delivered-confirm-dialog"
import { MarkPurchasedConfirmDialog } from "./mark-purchased-confirm-dialog"
import { MarkReadyToShipConfirmDialog } from "./mark-ready-to-ship-confirm-dialog"
import { RejectApplicationConfirmDialog } from "./reject-application-confirm-dialog"

/** OKS-35 — Submission-bearing collab states. Show "View submissions" CTA. */
const VIEW_SUBMISSIONS_STATES: BrandMissionCollabRow["state"][] = [
  "accepted",
  "delivered",
  "posted",
  "completed",
]

/**
 * Per-row action cell for the brand collabs panel.
 *
 * Renders state-conditional actions:
 *   - `posted` rows: `Mark complete` (primary) + `Flag issue` (outline).
 *   - `assigned` / `accepted` rows (Open): `Flag issue` only.
 *   - `applied` rows (Drop, OKS-32): `Accept` (primary) + `Reject`
 *     (outline destructive). `Flag issue` is suppressed here for row
 *     tidiness per UX Spec §1 #10 / §2.2 — the server-side
 *     `can_flag_issue` flag IS true (state guard widened to all non-
 *     terminal states), but we don't render the button alongside
 *     Accept/Reject.
 *   - `to_ship` rows (Drop, OKS-32): `Mark shipped` (primary) + `Flag issue`.
 *   - `shipped` rows (Drop, OKS-32): `Mark delivered` (primary) + `Flag issue`.
 *     The tracking-number string + edit-pencil affordance live in the
 *     last-activity cell of the panel (NOT here).
 *   - `delivered` rows (Drop, OKS-32): `Flag issue` only (creator's turn
 *     to mark posted).
 *   - terminal rows (`completed`, `declined`, `issue`, `rejected`): no
 *     actions; render a muted hint.
 *
 * The `can_*` flags are server-side computed (`Collabs::BrandRowSerializer#
 * can_brand_transition?`, shared by the mission panel and — OKS-1038 — the
 * crew member page). The FE never re-derives — if the server says
 * "no" the button doesn't render.
 */
interface BrandMissionCollabRowActionsProps {
  row: BrandMissionCollabRow
  missionId: number
  /**
   * OKS-1038 — how `can_mark_shipped` on a `to_ship` row renders.
   *   - `"dialog"` (default, the mission panel): `MarkShippedDialog`.
   *   - `"queue_link"` (the crew member page): a link to the Shipments queue.
   *     Packing needs the creator's address and sizes, which the member page
   *     never receives (PRD R2 / R8), and the OKS-984 no-tracking guard is the
   *     queue's own contract — so the member page sends the brand there.
   */
  shipAction?: "dialog" | "queue_link"
  /**
   * OKS-35 — Page-level callback that opens the BrandSubmissionsSheet
   * for this collab. When omitted, the View submissions CTA is hidden
   * (back-compat for any caller that hasn't wired the Sheet yet).
   */
  onViewSubmissions?: (collabId: number) => void
}

export function BrandMissionCollabRowActions({
  row,
  missionId,
  onViewSubmissions,
  shipAction = "dialog",
}: BrandMissionCollabRowActionsProps) {
  const { t } = useTranslation()

  // OKS-35 — Render the "View submissions (N/3)" pill when the collab
  // is in a submission-bearing state. Sits before the state-specific
  // primary actions (Approve/Flag/MarkComplete) so it's always
  // discoverable.
  const viewSubmissionsButton =
    onViewSubmissions && VIEW_SUBMISSIONS_STATES.includes(row.state) ? (
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => onViewSubmissions(row.id)}
        data-testid={`brand-mission-collab-view-submissions-${row.id}`}
      >
        <Eye aria-hidden="true" className="mr-1 size-4" />
        {t("submissions.brand.view_overflow", {
          count: row.submission_counts.total,
          cap: SUBMISSION_CAP,
        })}
      </Button>
    ) : null

  // Terminal states render nothing — no further actions in v1.
  // OKS-32 adds `rejected` to the terminal set (Drop-only). The
  // hint copy diverges per terminal state: `rejected` carries its
  // own variant ("Application rejected"); the rest share the
  // generic em-dash fallback. Bare-string `t(...)` calls here so
  // the OKS-8 i18n health-check static scanner picks up both keys
  // (no dynamic key indirection).
  // OKS-483 — `reopen_requested` rows render ONLY the Approve / Decline
  // reopen pair (Flag issue is suppressed for tidiness, same posture as
  // `applied`; the server `can_flag_issue` stays true). UX Spec §C.
  // OKS-1017 — `flex-wrap`: both triggers are nowrap and sit in a nowrap
  // `TableCell`, so inside the width-capped panel the pair pushed the table
  // past its box (« Refuser » clipped at rest at 1440; the group overflowed
  // the mobile card at 375). Wrapping lets « Refuser » drop under « Approuver
  // la réouverture » and lowers the cell's min-content to a single button.
  if (row.state === "reopen_requested") {
    return (
      <div className="flex flex-wrap items-center justify-end gap-2">
        {row.can_approve_reopen && (
          <ApproveReopenConfirmDialog row={row} missionId={missionId} />
        )}
        {row.can_decline_reopen && (
          <DeclineReopenConfirmDialog row={row} missionId={missionId} />
        )}
      </div>
    )
  }

  // OKS-483 — `expired` is terminal-but-visible on the brand panel: no brand
  // action (the creator drives the reopen via `request_reopen`), so it joins
  // the muted terminal-hint set.
  if (
    row.state === "completed" ||
    row.state === "declined" ||
    row.state === "issue" ||
    row.state === "rejected" ||
    row.state === "expired"
  ) {
    const hint =
      row.state === "rejected"
        ? t("missions.show.collabs.terminal_state_hint_rejected")
        : t("missions.show.collabs.terminal_state_hint")

    return (
      <span
        className="text-muted-foreground text-xs"
        data-testid="brand-mission-collab-no-actions"
        data-terminal-state={row.state}
      >
        {hint}
      </span>
    )
  }

  // OKS-32 — `applied` rows: Accept + Reject pair (suppress Flag issue
  // in this row for tidiness; the server still accepts the POST per
  // UX Spec §1 #10).
  if (row.state === "applied") {
    return (
      <div className="flex items-center justify-end gap-2">
        {row.can_accept_application && (
          <AcceptApplicationConfirmDialog row={row} missionId={missionId} />
        )}
        {row.can_reject_application && (
          <RejectApplicationConfirmDialog row={row} missionId={missionId} />
        )}
      </div>
    )
  }

  // OKS-1017 (fix-forward) — `flex-wrap`, same mechanism as the reopen pair:
  // up to three nowrap triggers (View submissions + the state primary + Flag
  // issue) set the shared Actions column's min-content, which pushed the
  // width-capped table ~200px past its box and hid « Signaler un problème »
  // on every assigned/posted row at rest.
  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {viewSubmissionsButton}
      {/* OKS-33 — Promotional primary actions per state. The shipping-arc
          actions (mark_shipped / mark_delivered) below are shared with
          Drop and gated only by `can_*` + state. */}
      {row.can_mark_purchased && row.state === "accepted" && (
        <MarkPurchasedConfirmDialog row={row} missionId={missionId} />
      )}
      {row.can_mark_ready_to_ship && row.state === "purchased" && (
        <MarkReadyToShipConfirmDialog row={row} missionId={missionId} />
      )}
      {/* OKS-32 — Drop primary actions per state (Promotional reuses these
          two from `to_ship` onward). */}
      {row.can_mark_shipped &&
        row.state === "to_ship" &&
        shipAction === "queue_link" && (
          <Link
            href={brandShipmentsPath()}
            className={cn(
              buttonVariants({ variant: "outline", size: "sm" }),
              "max-md:min-h-11",
            )}
            data-testid={`brand-mission-collab-ship-from-queue-${row.id}`}
          >
            <Truck aria-hidden="true" className="mr-1 size-4" />
            {t("crew.detail.collab.ship_from_queue")}
          </Link>
        )}
      {row.can_mark_shipped &&
        row.state === "to_ship" &&
        shipAction === "dialog" && (
          <MarkShippedDialog
            row={row}
            target={{ kind: "collab", missionId }}
            mode="mark"
            variantOptions={shippableVariantsFor(row.product)}
            product={row.product ?? null}
            // OKS-1021 D4 — the 44px mobile touch floor `size="sm"` does not
            // carry (OKS-874 deferral). OKS-1028 D4 gives the queue's two ship
            // triggers the same prop (`pages/Brand/Shipments/Index.tsx`).
            triggerClassName="max-md:min-h-11"
          />
        )}
      {row.can_mark_delivered && row.state === "shipped" && (
        <MarkDeliveredConfirmDialog row={row} missionId={missionId} />
      )}
      {/* OKS-34 — `mark_complete` from `posted`. */}
      {row.can_mark_complete && (
        <MarkCompleteConfirmDialog row={row} missionId={missionId} />
      )}
      {/* OKS-34 — `flag_issue` from any non-terminal state EXCEPT `applied`
          (handled above). Server gate is permissive across the widened
          from-set; UX hides on `applied` rows only. */}
      {row.can_flag_issue && (
        <FlagIssueDialog row={row} missionId={missionId} />
      )}
    </div>
  )
}
